import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Header } from '../components/layout/Header'

interface SearchResult {
  id: string
  type: string
  title: string
  description: string
  tags: string[]
  score: number
  match_type: string
}

interface AutocompleteItem {
  text: string
  type: string
  id: string | null
}

const API_BASE = '/api/advanced-search'

const AdvancedSearchPage: React.FC = () => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [autocomplete, setAutocomplete] = useState<AutocompleteItem[]>([])
  const [loading, setLoading] = useState(false)
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [searchTime, setSearchTime] = useState<number | null>(null)
  const [spellCheck, setSpellCheck] = useState<{ corrected: string; has_corrections: boolean } | null>(null)

  // Filters
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [fuzzyEnabled, setFuzzyEnabled] = useState(true)

  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<HTMLDivElement>(null)

  const typeOptions = ['market', 'report', 'seller', 'category', 'compliance']

  // Debounced autocomplete
  useEffect(() => {
    if (query.length < 2) {
      setAutocomplete([])
      return
    }

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/autocomplete?prefix=${encodeURIComponent(query)}`)
        if (res.ok) {
          setAutocomplete(await res.json())
        }
      } catch (err) {
        console.error('Autocomplete error:', err)
      }
    }, 150)

    return () => clearTimeout(timer)
  }, [query])

  // Close autocomplete on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowAutocomplete(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const q = searchQuery || query
    if (!q.trim()) return

    setLoading(true)
    setShowAutocomplete(false)

    try {
      // Check spelling first
      const spellRes = await fetch(`${API_BASE}/spell-check?q=${encodeURIComponent(q)}`)
      if (spellRes.ok) {
        setSpellCheck(await spellRes.json())
      }

      // Perform search
      const params = new URLSearchParams({
        q,
        fuzzy: String(fuzzyEnabled),
        limit: '20'
      })

      if (selectedTypes.length > 0) {
        params.set('types', selectedTypes.join(','))
      }

      const res = await fetch(`${API_BASE}/search?${params}`)
      if (res.ok) {
        const data = await res.json()
        setResults(data.results)
        setSuggestions(data.suggestions || [])
        setSearchTime(data.search_time_ms)
      }
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }, [query, selectedTypes, fuzzyEnabled])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  const styles: Record<string, React.CSSProperties> = {
    container: { minHeight: '100vh', backgroundColor: '#f5f7fa' },
    content: { padding: '32px', maxWidth: '1000px', margin: '0 auto' },
    title: { fontSize: '28px', fontWeight: 700, color: '#1F2937', marginBottom: '8px' },
    subtitle: { color: '#6B7280', marginBottom: '24px' },
    searchBox: {
      position: 'relative' as const,
      marginBottom: '24px'
    },
    searchInputWrapper: {
      display: 'flex',
      gap: '12px'
    },
    searchInput: {
      flex: 1,
      padding: '16px 20px',
      fontSize: '16px',
      border: '2px solid #E5E7EB',
      borderRadius: '12px',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    searchButton: {
      padding: '16px 32px',
      backgroundColor: '#4A90E2',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer'
    },
    autocompleteDropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      maxHeight: '300px',
      overflowY: 'auto' as const,
      zIndex: 100
    },
    autocompleteItem: {
      padding: '12px 16px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #F3F4F6'
    },
    filters: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap' as const,
      marginBottom: '16px',
      alignItems: 'center'
    },
    filterChip: {
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
      border: '1px solid #D1D5DB',
      backgroundColor: 'white'
    },
    filterChipActive: {
      backgroundColor: '#4A90E2',
      color: 'white',
      borderColor: '#4A90E2'
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginLeft: 'auto'
    },
    spellCheck: {
      padding: '12px 16px',
      backgroundColor: '#FEF3C7',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    meta: {
      fontSize: '14px',
      color: '#6B7280',
      marginBottom: '16px'
    },
    resultCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'box-shadow 0.2s'
    },
    resultTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#1F2937',
      marginBottom: '8px'
    },
    resultDescription: {
      color: '#6B7280',
      marginBottom: '12px'
    },
    resultMeta: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap' as const
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: 500
    },
    typeBadge: { backgroundColor: '#DBEAFE', color: '#2563EB' },
    scoreBadge: { backgroundColor: '#D1FAE5', color: '#059669' },
    tag: {
      padding: '4px 8px',
      backgroundColor: '#F3F4F6',
      borderRadius: '4px',
      fontSize: '12px',
      color: '#6B7280'
    },
    suggestions: {
      marginTop: '24px',
      padding: '16px',
      backgroundColor: '#F9FAFB',
      borderRadius: '8px'
    },
    suggestionTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '8px'
    },
    suggestionLink: {
      display: 'inline-block',
      padding: '6px 12px',
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '6px',
      marginRight: '8px',
      marginBottom: '8px',
      fontSize: '13px',
      color: '#4A90E2',
      cursor: 'pointer'
    },
    noResults: {
      textAlign: 'center' as const,
      padding: '40px',
      color: '#6B7280'
    }
  }

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>Advanced Search</h1>
        <p style={styles.subtitle}>Search with fuzzy matching and intelligent suggestions</p>

        <div style={styles.searchBox} ref={autocompleteRef}>
          <div style={styles.searchInputWrapper}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setShowAutocomplete(true)
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowAutocomplete(true)}
              placeholder="Search markets, reports, sellers..."
              style={styles.searchInput}
              data-search-input
            />
            <button
              onClick={() => handleSearch()}
              style={styles.searchButton}
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {showAutocomplete && autocomplete.length > 0 && (
            <div style={styles.autocompleteDropdown}>
              {autocomplete.map((item, idx) => (
                <div
                  key={idx}
                  style={styles.autocompleteItem}
                  onClick={() => {
                    setQuery(item.text)
                    setShowAutocomplete(false)
                    handleSearch(item.text)
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = '#F3F4F6'
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent'
                  }}
                >
                  <span>{item.text}</span>
                  <span style={{ ...styles.badge, ...styles.typeBadge }}>{item.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.filters}>
          {typeOptions.map((type) => (
            <span
              key={type}
              style={{
                ...styles.filterChip,
                ...(selectedTypes.includes(type) ? styles.filterChipActive : {})
              }}
              onClick={() => toggleType(type)}
            >
              {type}
            </span>
          ))}

          <div style={styles.toggle}>
            <label style={{ fontSize: '14px', color: '#6B7280' }}>
              <input
                type="checkbox"
                checked={fuzzyEnabled}
                onChange={(e) => setFuzzyEnabled(e.target.checked)}
                style={{ marginRight: '6px' }}
              />
              Fuzzy matching
            </label>
          </div>
        </div>

        {spellCheck?.has_corrections && (
          <div style={styles.spellCheck}>
            <span>Did you mean: </span>
            <strong
              style={{ cursor: 'pointer', color: '#4A90E2' }}
              onClick={() => {
                setQuery(spellCheck.corrected)
                handleSearch(spellCheck.corrected)
              }}
            >
              {spellCheck.corrected}
            </strong>
            ?
          </div>
        )}

        {searchTime !== null && (
          <div style={styles.meta}>
            Found {results.length} results in {searchTime}ms
          </div>
        )}

        {results.length > 0 ? (
          results.map((result) => (
            <div
              key={result.id}
              style={styles.resultCard}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={styles.resultTitle}>{result.title}</h3>
              <p style={styles.resultDescription}>{result.description}</p>
              <div style={styles.resultMeta}>
                <span style={{ ...styles.badge, ...styles.typeBadge }}>{result.type}</span>
                <span style={{ ...styles.badge, ...styles.scoreBadge }}>
                  {Math.round(result.score * 100)}% match
                </span>
                {result.tags.map((tag, idx) => (
                  <span key={idx} style={styles.tag}>{tag}</span>
                ))}
              </div>
            </div>
          ))
        ) : (
          query && !loading && (
            <div style={styles.noResults}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>No results found</p>
              <p>Try adjusting your search or enabling fuzzy matching</p>
            </div>
          )
        )}

        {suggestions.length > 0 && (
          <div style={styles.suggestions}>
            <div style={styles.suggestionTitle}>Related searches:</div>
            {suggestions.map((suggestion, idx) => (
              <span
                key={idx}
                style={styles.suggestionLink}
                onClick={() => {
                  setQuery(suggestion)
                  handleSearch(suggestion)
                }}
              >
                {suggestion}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdvancedSearchPage
